import type { PartDefinition, MaterialInfo } from "@rogue/rackbuilder"
import { getRackModel } from "../../../modelPaths"
import { RackOptions } from "../../../RackTypes"
import { PowerRackBuilder } from "../powerRack/powerRackBuilder"
import { FTPowerRackBuilder } from "../powerRack/ftPowerRackBuilder"
import { FTMaterialBuilder } from "./materials/ftMaterialBuilder"
import { createTrolleyClip, createFMRollerPart } from "./FM/ftSharedParts"

// Abstract base builder for all Functional Trainer variants
export abstract class FunctionalTrainerBuilder {
  protected config: RackOptions
  protected materialBuilder: FTMaterialBuilder
  protected baseRackBuilder: PowerRackBuilder
  
  constructor(config: RackOptions) {
    this.config = config
    this.materialBuilder = new FTMaterialBuilder(config)
    this.baseRackBuilder = new FTPowerRackBuilder(config)
  }
  
  public build(): PartDefinition {
    // Step 1: Build the base power rack structure
    const baseRack = this.baseRackBuilder.build()

    // Step 2: Adjust J-cup offsets if needed (E.g. Indy HR)
    this.adjustJCupOffsets(baseRack)

    // Step 3: Apply stainless uprights if configured
    this.applyStainlessUprights(baseRack)

    // Step 4: Reposition band pegs so they don't clip through the side pulleys
    this.repositionBandPegsForFT(baseRack)

    // Step 5: Add FT-specific components
    this.addFunctionalTrainerComponents(baseRack)

    return baseRack
  }

  // Band pegs clip through side pulleys at default z positions on specific rack configs
  protected repositionBandPegsForFT(baseRack: PartDefinition): void {
    if (!this.config.bandPegs || !this.config.sixPost) return

    this.repositionSideBandPegs(baseRack)

    const rightSide = baseRack.slots['other-side'] as PartDefinition
    if (rightSide) this.repositionSideBandPegs(rightSide)
  }

  private repositionSideBandPegs(frontUpright: PartDefinition): void {
    const bottomCrossmember = (frontUpright.slots['crossmember-bottom'] as PartDefinition[])?.[0]
    if (!bottomCrossmember) return

    const frontPeg = bottomCrossmember.slots.first as PartDefinition
    const backPeg  = bottomCrossmember.slots.last as PartDefinition

    if (frontPeg) frontPeg.offset = { z: -14 }
    if (backPeg) backPeg.offset = {}
  }

  // Public method to allow other builders to add components to this upright
  public addComponentsToLeftSide(
    materials: MaterialInfo,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    frontUpright: PartDefinition,
    backUpright: PartDefinition
  ): void {
    this.addLeftSide(materials, topCrossmember, bottomCrossmember, frontUpright, backUpright)
  }

  public addComponentsToRightSide(
    materials: MaterialInfo,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    frontUpright: PartDefinition,
    backUpright: PartDefinition
  ): void {
    this.addRightSide(materials, topCrossmember, bottomCrossmember, frontUpright, backUpright)
  }

  // Subclasses like IndyBuilder can override to adjust positioning for the HR version
  protected adjustJCupOffsets(baseRack: PartDefinition): void {
  // Default: no adjustments needed
  }
  
  // Subclasses typically don't override this - they override the individual build methods
  protected addFunctionalTrainerComponents(part: PartDefinition): void {
    // Create FT-specific materials once (with proper overrides)
    const materials = this.materialBuilder.createMainMaterial()
  
    // Add to left side
    this.addSideFromUpright(part, materials, true)
  
    // Add to right side
    const rightFrontUpright = part.slots['other-side'] as PartDefinition
    this.addSideFromUpright(rightFrontUpright, materials, false)
  }
  
  protected getFarthestBackUpright(part: PartDefinition): PartDefinition {
    const bottomCrossmember = this.getBottomCrossmember(part)
    const secondRowUpright = bottomCrossmember.slots.back as PartDefinition
  
    // 6-post rack: navigate to third row
    if (this.config.sixPost) {
      const backCrossmember = secondRowUpright.slots['crossmember-bottom'] as PartDefinition
      
      return backCrossmember.slots.back as PartDefinition
    }
  
    // 4-post rack: second row is the back
    return secondRowUpright
  }

  //  Apply stainless steel material to front two uprights only
  protected applyStainlessUprights(baseRack: PartDefinition): void {
    if (!this.config.stainlessUprights) {
      return
    }
    
    const stainlessMaterial = this.materialBuilder.createFrontUprightMaterial()
    
    // Apply to front left upright
    if (baseRack.materials) {
      baseRack.materials = { ...baseRack.materials, ...stainlessMaterial }
    }
    
    // Apply to front right upright
    const rightUpright = baseRack.slots['other-side']
    if (rightUpright && !Array.isArray(rightUpright) && rightUpright.materials) {
      rightUpright.materials = { ...rightUpright.materials, ...stainlessMaterial }
    }
  }

  private addSideFromUpright(frontUpright: PartDefinition, materials: MaterialInfo, left: boolean): void {
    const topCrossmember = this.getTopCrossmember(frontUpright)
    const bottomCrossmember = this.getBottomCrossmember(frontUpright)
    const backUpright = this.getFarthestBackUpright(frontUpright)
  
    if (left) {
      this.addLeftSide(materials, topCrossmember, bottomCrossmember, frontUpright, backUpright)
    } else {
      this.addRightSide(materials, topCrossmember, bottomCrossmember, frontUpright, backUpright)
    }
  }

  // MARK: Side Assembly
  protected addLeftSide(
    materials: MaterialInfo,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    frontUpright: PartDefinition,
    backUpright: PartDefinition
  ): void {
    this.addSideComponents(materials, topCrossmember, bottomCrossmember, frontUpright, backUpright, true)
  }
  
  protected addRightSide(
    materials: MaterialInfo,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    frontUpright: PartDefinition,
    backUpright: PartDefinition
  ): void {
    this.addSideComponents(materials, topCrossmember, bottomCrossmember, frontUpright, backUpright, false)
  }
  
  protected addSideComponents(
    materials: MaterialInfo,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    frontUpright: PartDefinition,
    backUpright: PartDefinition,
    left: boolean
  ): void {
    const halfRack = !this.config.sixPost
    
    // Build components (variants override these methods)
    const sideMeshes = this.buildSideMeshes(materials, left)
    const frontBottomPulley = this.buildFrontBottomPulley(materials, left)
    
    // Attach to rack structure
    if (halfRack) {
      this.attachToHalfRack(sideMeshes, frontBottomPulley, topCrossmember, bottomCrossmember, left)
    } else {
      this.attachToFullRack(sideMeshes, frontBottomPulley, topCrossmember, bottomCrossmember, materials, left)
    }
    
    if (left) {
      this.addResistanceSystem(backUpright, materials)
    }
    
    if (this.config.rackRoller && left) {
      const backRollerUpright =  bottomCrossmember.slots.back as PartDefinition
      this.addRackRoller(halfRack ? frontUpright : backRollerUpright)
    }
  }
  
  // Abstract Methods (Must Override)
  //  Build side meshes (pulleys, shrouds, stack poles)
  protected abstract buildSideMeshes(materials: MaterialInfo, left: boolean): PartDefinition[]
  
  // Different for standard vs twin vs 80" height
  protected abstract buildFrontBottomPulley(materials: MaterialInfo, left: boolean): PartDefinition
  
  // Different stack types: rhino footplate, 250, 300
  protected abstract addResistanceSystem(backUpright: PartDefinition, materials: MaterialInfo): void
  
  protected abstract attachToHalfRack(
    sideMeshes: PartDefinition[],
    frontBottomPulley: PartDefinition,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    left: boolean
  ): void
  
  protected abstract attachToFullRack(
    sideMeshes: PartDefinition[],
    frontBottomPulley: PartDefinition,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    materials: MaterialInfo,
    left: boolean
  ): void
  
  
  // Standard: 'FM', Twin: 'FM_TWIN', etc.
  protected abstract getModelPrefix(): string
  
  //  Get the slot name for side meshes
  protected getSideSlotName(): string {
    return 'side_2' // Get the slot name for side meshes: Standard: 'side_2', Twin: 'side_3'
  }
  
  // Build half rack top pulley
  protected buildHalfRackTopPulley(materials: MaterialInfo, left: boolean): PartDefinition {
    const plate = this.buildHalfRackTopPulleyPlate(left)
    
    return {
      mesh        : getRackModel(this.getModelPrefix(), 'halfrack top pulley'),
      slots       : { plate },
      materials,
      offset      : { x: 0, z: 2 },
      adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
    }
  }
  
  protected buildHalfRackTopPulleyPlate(left: boolean): PartDefinition {
    return {
      mesh        : getRackModel(this.getModelPrefix(), `halfrack top pulley ${left ? 'left' : 'right'}`),
      adjustments : left ? [] : [{ type: 'flip', axis: 'X' }],
      slots       : {},
    }
  }
  
  // MARK: Attachments
  // Thin wrapper so IndyBuilder can call createTrolleyClip via `this`
  protected buildTrolleyClip(left: boolean): PartDefinition {
    return createTrolleyClip(left, this.config.trolleySelection)
  }

  protected addRackRoller(targetUpright: PartDefinition): void {
    targetUpright.slots['safety-low'] = createFMRollerPart()
  }
  
  protected buildSharedCarriage(trolleyClip: PartDefinition, materials: MaterialInfo, left: boolean): PartDefinition {
    const brandingPlate: PartDefinition = {
      mesh        : getRackModel('FM_SHARED', 'carriage branding plate'),
      slots       : {},
      anchor      : left ? '' : 'front',
      offset      : left ? {} : { z: 0.1 },
      adjustments : left ? [] : [{ type: 'flip', axis: 'Z' }],
    }

    if (!left) {
      const cover: PartDefinition = {
        mesh        : getRackModel('FM_SHARED', 'carriage cover'),
        slots       : {},
        offset      : this.config.halfRack ? {} : { x: -0.4 },
        adjustments : [{ type: 'flip', axis: 'X' }, { type: 'flip', axis: 'Y' }],
      }

      brandingPlate.slots.cover = cover
    }

    return {
      mesh  : getRackModel('FM_SHARED', 'carriage'),
      materials,
      slots : {
        cable    : { mesh: getRackModel('FM_CABLES', `carriage cable ${this.config.height}`), slots: {} },
        trolley  : this.config.trolleySelection ? trolleyClip : [],
        branding : brandingPlate,
      },
    }
  }
 
  protected getDepthInInches(): string {
    if (this.config.depth === '4') return '43'
    if (this.config.depth === '2') return '24'
    
    return '30'
  }
  
  protected getBackBottomCrossmember(bottomCrossmember: PartDefinition): PartDefinition {
    const middleBackUpright = bottomCrossmember.slots.back as PartDefinition
    
    return middleBackUpright.slots['crossmember-bottom'] as PartDefinition
  }
  
  protected getTopCrossmember(part: PartDefinition): PartDefinition {
    return (part.slots['crossmember-top'] as PartDefinition[])[0]
  }
  
  protected getBottomCrossmember(part: PartDefinition): PartDefinition {
    return (part.slots['crossmember-bottom'] as PartDefinition[])[0]
  }
  
  protected getFrontLeftUpright(part: PartDefinition): PartDefinition {
    return part
  }
  
  /**
   * Get the back upright where FT components attach
   * For 6-post racks (storage=true), this is the third row upright
   * For 4-post racks (storage=false), this is the second row upright
   */
  protected getBackUpright(part: PartDefinition): PartDefinition {
    const bottomCrossmember = this.getBottomCrossmember(part)
    const middleBackUpright = bottomCrossmember.slots.back as PartDefinition
  
    // If storage (6-post rack), navigate to third row upright
    if (this.config.sixPost) {
      const backBottomCrossmember = middleBackUpright.slots['crossmember-bottom'] as PartDefinition
      
      return backBottomCrossmember.slots.back as PartDefinition
    }
  
    // For 4-post racks (no storage), middle back upright is the target
    return middleBackUpright
  }
}